import { assetUrl } from "./asset-paths.js";
import { AssetCache } from "./cache.js";
import { MapRenderer } from "./renderer.js";
import { logger, errorMessage } from "./log.js";
import { fetchJson } from './http.js';
import { bindDebugControls } from './debug-controls.js';
import { bindPerformanceOverlay } from './performance-overlay.js';
import { createViewerWorker } from './viewer-worker.js';
const log = logger("App");
const $ = (id) => document.getElementById(id),
    diagnostic = (message) => {
        log.warn("diagnostic", { message, loadId: generation });
        if ($("messages").childElementCount >= 200) return;
        const li = document.createElement("li");
        li.textContent = message;
        $("messages").append(li);
        $("diagnostic-count").textContent = $("messages").childElementCount;
    };
const cache = new AssetCache(diagnostic);
let renderer,
    worker,
    abort,
    generation = 0,
    sequence = 0,
    selected,
    sceneData,
    searchGeneration = 0;
const pending = new Map();
function status(message) {
    log.debug("status", { loadId: generation, stage: message });
    $("status").textContent = message;
}
log.info("startup", { userAgent: navigator.userAgent, worker: typeof Worker });
window.addEventListener("error", event => log.error("uncaught exception", event));
window.addEventListener("unhandledrejection", event => log.error("unhandled rejection", event.reason));
try {
    if (!$("canvas").getContext("webgl2"))
        throw new Error(
            "WebGL2 is unavailable. Enable browser hardware acceleration and reload.",
        );
    renderer = new MapRenderer($("canvas"), diagnostic);
} catch (e) {
    log.error("renderer initialization failed", e);
    status(errorMessage(e));
}
bindDebugControls(renderer);
bindPerformanceOverlay(renderer);
function cancel() {
    log.info("cancel load", { loadId: generation, requests: pending.size });
    generation++;
    if (renderer) renderer.loading = false;
    abort?.abort();
    worker?.terminate();
    worker = null;
    for (const p of pending.values())
        p.reject(new DOMException("Cancelled", "AbortError"));
    pending.clear();
    cache.active.clear();
    $("cancel").hidden = true;
}
function rpc(type, data) {
    const requestId = ++sequence;
    log.debug("worker request", { type, loadId: generation, requestId, ...data });
    return new Promise((resolve, reject) => {
        pending.set(requestId, { resolve, reject });
        try {
            worker.postMessage({ type, loadId: generation, requestId, ...data });
        } catch (error) {
            pending.delete(requestId);
            log.error("worker request could not be sent", error, { type, requestId });
            reject(error);
        }
    });
}
async function loadMap(id, retry = 0) {
    if (!renderer) return;
    cancel();
    renderer.clear();
    renderer.loading = true;
    sceneData = null;
    closeSuggestions();
    $("clear-map").hidden = false;
    const loadId = generation;
    const started = performance.now();
    let parserStage = "Starting parser worker";
    log.info("load map", { id, retry, loadId });
    abort = new AbortController();
    const signal = abort.signal;
    selected = id;
    $("messages").replaceChildren();
    $("diagnostic-count").textContent = "0";
    $("retry").hidden = true;
    $("cancel").hidden = false;
    $("welcome").hidden = true;
    status("Loading map details…");
    try {
        const map = await fetchJson(`/maps/${encodeURIComponent(id)}`, {
            signal,
            cache: "no-cache",
        });
        log.info("map details", { loadId, name: map.name, size: map.size, revision: map.revision });
        if (loadId !== generation) return;
        selected = map.name;
        const params = new URLSearchParams(location.hash.slice(1));
        if (params.get("map") === id && id !== map.name) {
            params.set("map", map.name);
            history.replaceState(null, "", `#${params}`);
        }
        $("map-name").textContent = map.name;
        $("search").value = mapLabel(map);
        status("Downloading BSP…");
        const bytes = await cache.fetch(map.bspUrl, signal, (size, total) =>
            status(
                `Downloading BSP · ${(size / 1048576).toFixed(1)}${total ? ` / ${(total / 1048576).toFixed(1)}` : ""} MiB`,
            ),
        );
        if (loadId !== generation) return;
        log.info("BSP downloaded", { loadId, bytes: bytes.byteLength });
        worker = createViewerWorker('worker');
        const parserFailure = (error) => {
            if (loadId !== generation) return;
            const message = errorMessage(error,
                "Map parser worker failed to start or crashed. See the browser console for details.");
            log.error("parser worker failure", error, { message, loadId, map: map.name, stage: parserStage });
            diagnostic(`${message} Last stage: ${parserStage}`);
            cancel();
            status(`Parser error: ${message}`);
            $("retry").hidden = false;
            $("cancel").hidden = true;
        };
        worker.onerror = parserFailure;
        worker.onmessageerror = () => parserFailure(new Error("Unable to deserialize parser worker data"));
        worker.onmessage = async ({ data }) => {
            if (data.loadId !== generation) return;
            log.debug("worker response", { loadId, type: data.type, requestId: data.requestId });
            if (data.type === "asset" || data.type === "particle-index") {
                try {
                    const url = data.type === "particle-index" ? `/particles?assetSet=${map.assetSet}&map=${encodeURIComponent(map.name)}`
                        : assetUrl(data.path, map.assetSet),
                        bytes = await cache.fetch(url, signal);
                    if (loadId === generation)
                        worker.postMessage(
                            {
                                type: "asset",
                                requestId: data.requestId,
                                bytes: bytes.buffer,
                            },
                            [bytes.buffer],
                        );
                } catch (e) {
                    log.error("asset request failed", e, { loadId, path: data.path });
                    if (e.message === "asset_revision_changed" && retry < 1) {
                        void loadMap(id, retry + 1);
                        return;
                    }
                    if (loadId === generation)
                        worker.postMessage({
                            type: "asset",
                            requestId: data.requestId,
                            error: errorMessage(e),
                        });
                }
                return;
            }
            if (data.type === "progress") {
                parserStage = data.stage;
                status(data.stage);
                return;
            }
            if (data.type === "diagnostic") {
                diagnostic(data.message);
                return;
            }
            if (data.requestId) {
                const p = pending.get(data.requestId);
                if (p) {
                    pending.delete(data.requestId);
                    data.type === "error"
                        ? p.reject(Object.assign(new Error(data.message), data.error))
                        : p.resolve(data);
                }
                return;
            }
            if (data.type === "error") {
                parserFailure(Object.assign(new Error(data.message), data.error));
                return;
            }
            if (data.type === "geometry") {
                log.info("geometry received", { loadId, faces: data.geometry.length, props: data.props.length });
                sceneData = data;
                status("Loading materials…");
                const active = () => loadId === generation;
                const mapRpc = (type, args) =>
                    active()
                        ? rpc(type, args)
                        : Promise.reject(
                              new DOMException("Cancelled", "AbortError"),
                          );
                try {
                    await renderer.world(data, mapRpc, active, (message) => {
                        if (active()) status(message);
                    });
                    if (!active()) return;
                    restoreCamera(map.name);
                    status("Loading props…");
                    await renderer.props(
                        data.props,
                        mapRpc,
                        active,
                        (message) => {
                            if (active()) status(message);
                        },
                    );
                    if (!active()) return;
                    renderer.startMapParticles(mapRpc);
                    status(
                        "Click the map to explore",
                    );
                    $("cancel").hidden = true;
                    renderer.loading = false;
                    log.info("map ready", { loadId, name: map.name, durationMs: performance.now() - started });
                    await updateUsage();
                } catch (e) {
                    log.error("scene load failed", e, { loadId });
                    if (active()) {
                        renderer.loading = false;
                        diagnostic(errorMessage(e));
                        status("Map loading failed. Retry to start again.");
                        $("retry").hidden = false;
                    }
                }
            }
        };
        worker.postMessage({ type: "load", loadId, mapName: map.name, bytes: bytes.buffer }, [
            bytes.buffer,
        ]);
    } catch (e) {
        log.error("map load failed", e, { loadId, stage: parserStage });
        if (loadId !== generation || e.name === "AbortError") return;
        renderer.loading = false;
        if (e.message === "asset_revision_changed" && retry < 1) {
            void loadMap(id, retry + 1);
            return;
        }
        status(errorMessage(e));
        $("retry").hidden = false;
        $("cancel").hidden = true;
    }
}
let suggestions = [],
    highlighted = -1,
    searchAbort;
function mapLabel(map) {
    return `${map.name} — ${(map.size / 1048576).toFixed(1)} MiB`;
}
function closeSuggestions() {
    clearTimeout(debounce);
    searchGeneration++;
    searchAbort?.abort();
    $("maps").hidden = true;
    $("search-status").textContent = "";
    $("search").setAttribute("aria-expanded", "false");
    $("search").removeAttribute("aria-activedescendant");
}
function highlight(index) {
    highlighted = index;
    for (const [i, option] of [...$("maps").children].entries())
        option.setAttribute("aria-selected", String(i === index));
    const option = $("maps").children[index];
    if (option) {
        $("search").setAttribute("aria-activedescendant", option.id);
        option.scrollIntoView({ block: "nearest" });
    }
}
function choose(map) {
    $("search").value = mapLabel(map);
    $("search-status").textContent = "";
    history.replaceState(null, "", `#map=${encodeURIComponent(map.name)}`);
    void loadMap(map.name);
}
function clearMap() {
    log.info("clear selected map");
    cancel();
    renderer?.clear();
    sceneData = null;
    selected = null;
    if (document.pointerLockElement) document.exitPointerLock();
    closeSuggestions();
    $("search").value = "";
    $("search-status").textContent = "";
    $("clear-map").hidden = true;
    $("retry").hidden = true;
    $("welcome").hidden = false;
    $("map-name").textContent = "No map selected";
    $("messages").replaceChildren();
    $("diagnostic-count").textContent = "0";
    history.replaceState(null, "", location.pathname + location.search);
    status("Select a map to explore.");
}
async function list() {
    log.debug("search map catalog");
    searchAbort?.abort();
    searchAbort = new AbortController();
    const own = ++searchGeneration;
    $("search-status").textContent = "Searching maps…";
    try {
        const url = new URL("/maps", location.origin);
        url.searchParams.set("q", $("search").value);
        url.searchParams.set("limit", "50");
        const data = await fetchJson(url, { signal: searchAbort.signal, cache: 'no-store' });
        if (own !== searchGeneration) return;
        suggestions = data.maps;
        log.debug("catalog results", { count: suggestions.length, more: data.nextCursor !== null });
        highlighted = -1;
        $("maps").replaceChildren();
        for (const [index, map] of suggestions.entries()) {
            const option = document.createElement("li");
            option.id = `map-option-${index}`;
            option.setAttribute("role", "option");
            option.setAttribute("aria-selected", "false");
            const name = document.createElement("span"),
                size = document.createElement("span");
            name.textContent = map.name;
            size.textContent = `${(map.size / 1048576).toFixed(1)} MiB`;
            option.append(name, size);
            option.onmousedown = (event) => event.preventDefault();
            option.onclick = () => choose(map);
            $("maps").append(option);
        }
        $("maps").hidden = suggestions.length === 0;
        $("search").setAttribute(
            "aria-expanded",
            String(suggestions.length > 0),
        );
        $("search-status").textContent = !suggestions.length
            ? "No matching maps."
            : data.nextCursor !== null
              ? "Type more of the map name to narrow the results."
              : "";
    } catch (e) {
        if (e.name !== "AbortError") log.error("catalog request failed", e);
        if (own === searchGeneration && e.name !== "AbortError")
            $("search-status").textContent = e.message;
    }
}
function restoreCamera(id) {
    const params = new URLSearchParams(location.hash.slice(1));
    if (params.get("map") !== id) return;
    const p = params.get("camera")?.split(",").map(Number);
    if (
        p?.length === 5 &&
        p.every(Number.isFinite) &&
        p.slice(0, 3).every((x) => Math.abs(x) < 1e7)
    ) {
        renderer.camera.position.fromArray(p);
        renderer.yaw = p[3];
        renderer.pitch = Math.max(-1.55, Math.min(1.55, p[4]));
        log.debug("saved camera restored");
    }
}
async function updateUsage() {
    try {
        $("cache-size").textContent =
            `${((await cache.usage()) / 1048576).toFixed(1)} MiB saved on this browser`;
    } catch (error) {
        log.error("cache usage unavailable", error);
    }
}
let debounce;
$("search").oninput = () => {
    closeSuggestions();
    clearTimeout(debounce);
    debounce = setTimeout(list, 200);
};
$("search").onfocus = () => {
    if (selected && $("search").value.includes(" — ")) $("search").value = "";
    void list();
    $("search").select();
};
$("search").onblur = closeSuggestions;
$("search").onkeydown = (event) => {
    if (event.key === "Escape") {
        closeSuggestions();
        return;
    }
    if ($("maps").hidden) return;
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
        event.preventDefault();
        highlight(
            (highlighted +
                (event.key === "ArrowDown" ? 1 : -1) +
                suggestions.length) %
                suggestions.length,
        );
    } else if (event.key === "Enter" && suggestions.length) {
        event.preventDefault();
        choose(suggestions[Math.max(0, highlighted)]);
    }
};
$("clear-map").onclick = () => {
    clearMap();
    $("search").focus();
};
$("cancel").onclick = () => {
    cancel();
    status("Loading cancelled.");
    $("retry").hidden = false;
};
$("retry").onclick = () => selected && loadMap(selected);
$("reset").onclick = () =>
    sceneData && renderer.reset(sceneData.entities, sceneData.models);
$("speed").oninput = (e) => {
    log.debug("flight speed changed", { value: Number(e.target.value) });
    if (renderer) renderer.speed = Number(e.target.value);
};
$("sensitivity").oninput = (event) => {
    const value = Number(event.target.value);
    log.debug("mouse sensitivity changed", { value });
    if (renderer) renderer.sensitivity = value;
    $("sensitivity-value").textContent = `${value.toFixed(1)}×`;
};
$("quality").onchange = (e) => {
    log.debug("resolution changed", { pixelRatio: Number(e.target.value) });
    renderer?.renderer.setPixelRatio(Number(e.target.value));
};
$("share").onclick = async () => {
    if (!selected) return;
    const camera = [
        ...renderer.camera.position.toArray(),
        renderer.yaw,
        renderer.pitch,
    ].map((x) => x.toFixed(3));
    const url = new URL(location.href);
    url.hash = new URLSearchParams({ map: selected, camera: camera.join(",") });
    history.replaceState(null, "", url);
    try {
        await navigator.clipboard.writeText(url.href);
        log.debug("view link copied");
        status("View link copied.");
    } catch (error) {
        log.warn("clipboard unavailable", { message: errorMessage(error) });
        status("The address bar now contains the shareable view link.");
    }
};
$("clear-cache").onclick = async () => {
    await cache.clear();
    await updateUsage();
};
$("canvas").addEventListener("webglcontextlost", (e) => {
    log.warn("WebGL context lost");
    e.preventDefault();
    cancel();
    status("Graphics context lost. Waiting for recovery…");
});
$("canvas").addEventListener(
    "webglcontextrestored",
    () => {
        log.info("WebGL context restored");
        if (selected) void loadMap(selected);
    },
);
void updateUsage();
const initial = new URLSearchParams(location.hash.slice(1)).get("map");
if (initial) void loadMap(initial);
window.addEventListener("hashchange", () => {
    const id = new URLSearchParams(location.hash.slice(1)).get("map");
    if (id && id !== selected) void loadMap(id);
    else if (id) restoreCamera(id);
    else clearMap();
});
